'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { Database, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';


const REMINDER_KEY = 'backup_reminder_dismissed_at';
const REMINDER_INTERVAL_DAYS = 7;

export default function BackupReminderBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [daysSince, setDaysSince] = useState<number | null>(null);

  const check = useCallback(() => {
    if (typeof window === 'undefined') return;
    const dismissed = localStorage.getItem(REMINDER_KEY);
    if (!dismissed) {
      setShow(true);
      setDaysSince(null);
      return;
    }
    const diff = (Date.now() - Number(dismissed)) / (1000 * 60 * 60 * 24);
    if (diff >= REMINDER_INTERVAL_DAYS) {
      setShow(true);
      setDaysSince(Math.floor(diff));
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const dismiss = () => {
    localStorage.setItem(REMINDER_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="flex items-start gap-3 bg-accent/5 border border-accent/20 rounded-xl px-4 py-3 animate-slide-up">
      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Database size={15} className="text-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-600 text-foreground">
          {daysSince !== null ? `Database backup reminder — ${daysSince} days since last backup` : 'Database backup reminder'}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Export a full backup of your financial data to keep it safe. Go to <strong>Reports → Export Backup</strong> to download CSV, Excel, or PDF.
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href="/reports"
          className="flex items-center gap-1.5 text-xs font-600 text-accent hover:text-accent/80 transition-colors px-2 py-1 rounded-md hover:bg-accent/10"
        >
          <CheckCircle2 size={12} />
          Export Now
        </a>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
          aria-label="Dismiss backup reminder"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
